import React, {useState, useEffect, useRef} from 'react';
import Fetch from '@/utils/Fetch';
import Helper from '@/utils/helper';
import ItemComments from '@/components/shared-views/item-comments';
import JobPropertiesColumn from '@/components/job/job-properties-column';
import ManageInventory from '@/components/modals/inventory/manage-inventory';
import CustomerContactLocationSelector from '@/components/selectors/customer/customer-contact-location-selector';
import ManageSignatures from '@/components/modals/jobcard/manage-signatures';
import * as Enums from '@/utils/enums';
import JobHistory from '@/PageComponents/Job/JobHistory';
import Storage from '@/utils/storage';
import MiscService from '@/services/misc-service';
import ManageJobType from '@/components/modals/jobcard/manage-job-type';
import PS from '@/services/permission/permission-service';
import featureService from '@/services/feature/feature-service';
import constants from '@/utils/constants';
import CustomerService from '@/services/customer/customer-service';
import FormDefinitionsForItem from '@/components/modals/form-definition/form-definitions-for-item';
import ChangeStore from '@/components/modals/jobcard/change-store';
import ConfirmAction from '@/components/modals/confirm-action';
import JobRating from '@/components/job/rating/job-rating';
import FeedbackService from '@/services/feedback-service';
import SCComboBox from '@/components/sc-controls/form-controls/sc-combobox';
import SCDatePicker from '@/components/sc-controls/form-controls/sc-datepicker';
import StoreSelector from '@/components/selectors/store/store-selector';
import {
    Box,
    Button,
    Collapse,
    Flex,
    Text,
    UnstyledButton
} from "@mantine/core";
import {
    IconCalendarPlus,
    IconCheckbox,
    IconChevronRight,
    IconClockPlay,
    IconDeviceDesktop,
    IconFileDollar,
    IconFileInvoice,
    IconLinkPlus,
    IconPackage,
    IconPlus,
    IconReceipt,
    IconSignature
} from "@tabler/icons-react";
import { showNotification } from '@mantine/notifications';
import ManageJobInventoryComponent from '@/PageComponents/JobInventory/ManageJobInventoryComponent';
import LinkedItemsList from "@/PageComponents/Links/LinkedItemsList";
import JobInventoryInvoicedWidgetLite from '@/PageComponents/JobInventory/JobInventoryInvoicedWidgetLite';
import JobSectionCard from "@/PageComponents/Job/JobSectionCard";
import ChipButton from "@/PageComponents/Button/ChipButton";
import JobStatusService from '@/services/job/job-status-service';
import { useQuery } from '@tanstack/react-query';
import companyService from '@/services/company-service';
import CardedAppointmentWidget from "@/components/sc-controls/widgets/new/carded-appointment-widget";
import styles from '@/PageComponents/Job/job-details.module.css';
import TaskItems from "@/components/task/task-items";
import ScTextAreaControl from "@/components/sc-controls/form-controls/v2/ScTextAreaControl";
import { JobCardLengths } from '@/utils/fieldLengths/fieldLengths';

// Request declared outside component per guidelines
const fetchCurrencySymbol = async () => {
  try {
    return await companyService.getCurrencySymbol();
  } catch {
    return 'R';
  }
};

// Minimal TS conversion: props typed as any to avoid broad refactors
function JobDetails(props: any) {

  const [masterOfficeAdminPermission] = useState(PS.hasPermission(Enums.PermissionName.MasterOfficeAdmin));

  // Fetch company currency symbol once at job level and pass down
  const { data: currencySymbol } = useQuery(['company', 'currencySymbol'], fetchCurrencySymbol, { refetchOnWindowFocus: false, staleTime: 12 * 60 * 60 * 1000 });
  const [editJobPermission] = useState(PS.hasPermission(Enums.PermissionName.EditJob));
  const [manageCostingPermission] = useState(PS.hasPermission(Enums.PermissionName.ManageCosting));
  const { data: hasStockControl } = useQuery(['hasStockControl'], () => featureService.hasFeature(constants.features.STOCK_CONTROL));


  const [savingItems, setSavingItems] = useState(false);

  const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());

  const [nextStatuses, setNextStatuses] = useState<any[]>([]);
  const [nextStatusCanProceed, setNextStatusCanProceed] = useState(true);

  const [updateSignature, setUpdateSignature] = useState(0);
  const [updateForms, setUpdateForms] = useState(0);

  const getNextStatuses = () => {
    let items = props.nextJobStatuses.filter((x: any) => x.IsActive);
    setNextStatusCanProceed(!items.every((x: any) => x.CanProceed == false));

    items.map((item: any) => {
      item.disabled = !item.CanProceed;
      return item;
    });
    setNextStatuses(items);
  };

  useEffect(() => {
    getNextStatuses();
  }, [props.nextJobStatuses]);

  useEffect(() => {
    getNextStatuses();
    getStore();
    getFeedback();

    setHasEmployee(Storage.hasCookieValue(Enums.Cookie.employeeID));

  }, []);

  useEffect(() => {
    getJobStatusColors();
  }, [props.job.JobStatus]);

  const [jobStatusBackground, setJobStatusBackgound] = useState<string | undefined>(undefined);
  const [jobStatusColor, setJobStatusColor] = useState<string | undefined>(undefined);
  const [hasEmployee, setHasEmployee] = useState<boolean | undefined>(undefined);

  const getJobStatusColors = () => {
    let displayColor = props.job.JobStatus?.DisplayColor;

    const { color, backgroundColor } = MiscService.getStatusColors(displayColor);

    setJobStatusBackgound(backgroundColor);
    setJobStatusColor(color);
  };

  // JOB TYPE

  const [jobTypes, setJobTypes] = useState<any[]>(props.jobTypes);

  const setSelectedJobType = (e: any) => {
    props.updateJob("JobTypeID", e.ID);
  };

  const [showManageJobType, setShowManageJobType] = useState(false);

  const addNewJobType = () => {
    setShowManageJobType(true);
  };

  const onJobTypeSave = (jobType: any) => {
    if (jobType) {
      setJobTypes([...(jobTypes || []), jobType]);
      showNotification({
        title: 'Saved',
        message: 'Job type created successfully',
        color: 'scBlue',
        autoClose: 3000,
        withCloseButton: true,
      });
      props.updateJob("JobTypeID", jobType.ID);
    }
    setShowManageJobType(false);
  };

  const [showManageInventory, setShowManageInventory] = useState<string | null>(null);

  const addNewInventory = (mode: string) => {
    setShowManageInventory(mode);
  };

  const onInventorySave = (inventory: any) => {
    if (inventory) {
      if (showManageInventory === "jobInventory") {
        addJobInventoryItem(inventory);
      }
    }
    setShowManageInventory(null);
  };

  const jobDesc = props.job.Description;

  const handleJobDescChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if(e.currentTarget) {
          props.updateJob("Description", e.currentTarget.value);
      }
  };

  const startDate = props.job.StartDate;

  const handleDateChange = (e: any) => {
    props.updateJob("StartDate", e.value);
  };

  const [showSLADates] = useState(props.job.SLAStartDateTime || props.job.SLAEndDateTime);

  const [submitting, setSubmitting] = useState(false);

  async function submitComment() {
    if (props.accessStatus === Enums.AccessStatus.LockedWithAccess || props.accessStatus === Enums.AccessStatus.LockedWithOutAccess) {
      return;
    }

    setSubmitting(true);
    props.submitComment();
    setSubmitting(false);
  }

  const handleJobPropertyChange = (propertyName: string, value: any, orKeyValues?: any) => {
    props.updateJob(propertyName, value, orKeyValues);
  };

  /* CONTACT */

  // Use a ref so updateContact always compares against the latest contact,
  // even when called via a stale closure (e.g. the memoized handleSelectedOption
  // in contact-selector.tsx, which only re-binds when its contacts list changes).
  const contactRef = useRef(props.contact);
  useEffect(() => { contactRef.current = props.contact; }, [props.contact]);

  const updateContact = (contact: any) => {
    props.setContact(contact);
    // Business rule: only persist when a contact is actually selected.
    if (!!contact && contactRef.current?.ID !== contact.ID) {
      props.updateJob(null, null, [{
        key: 'Contact',
        value: contact
      }, {
        key: 'CustomerContactID',
        value: contact.ID
      }]);
    }
  };

  /* LOCATION */

  const updateLocation = (location: any) => {
    props.setLocation(location);

    if (CustomerService.hasLocationChanged(props.location, location)) {
      props.updateJob(null, null, [{
        key: 'Location',
        value: location
      }, {
        key: 'LocationID',
        value: location ? location.ID : null
      }]);
    }
  };

  const [showSignaturesModal, setShowSignaturesModal] = useState(false);

  const addJobInventoryItem = (jobInventoryItem: any) => {
    if (jobInventoryItem) {
      let oldInventory = !props.job.JobInventory ? [] : [...props.job.JobInventory];
      oldInventory.push(jobInventoryItem);
      props.updateJob("JobInventory", oldInventory);
    }
  };

  // STORES

  const [isMultiStore, setIsMultiStore] = useState(false);
  const [selectedStore, setSelectedStore] = useState<any>(props.job.Store ? props.job.Store : null);
  const [initialStore, setInitialStore] = useState<any>(props.job.Store ? props.job.Store : null);
  const [stores, setStores] = useState<any[]>([]);
  const [storesTotalResults, setStoresTotalResults] = useState<number | undefined>();

  const [storeChangePermission] = useState(PS.hasPermission(Enums.PermissionName.StoreChangeJob));
  const firstStoreUpdate = useRef(true);

  const getStore = async () => {
    const storesResult = await Fetch.get({
      url: `/Store/GetEmployeeStores?employeeID=${Storage.getCookie(Enums.Cookie.employeeID)}&searchPhrase=`,
    });
    setIsMultiStore(storesResult.TotalResults > 1);
    if (storesResult.TotalResults > 1) {
      // set store from job
      if (!Helper.isNullOrUndefined(props.job.StoreID) && !props.job.Store) {
        const storeResult = await Fetch.get({
          url: `/Store/${props.job.StoreID}`,
        });
        setSelectedStore(storeResult);
        setInitialStore(storeResult);
      }

      setStores(storesResult.Results);
      setStoresTotalResults(storesResult.TotalResults);
    }
  };

  const [searching, setSearching] = useState(false);
  const [storeSearch, setStoreSearch] = useState(props.job.Store ? props.job.Store.Name : '');

  const searchStores = async () => {
    setSearching(true);
    const storesResult = await Fetch.get({
      url: `/Store/GetEmployeeStores?employeeID=${Storage.getCookie(Enums.Cookie.employeeID)}&searchPhrase=${storeSearch}`,
    });
    setStores(storesResult.Results);
    setStoresTotalResults(storesResult.TotalResults);
    setSearching(false);
  };

  const [showChangeStoreModal, setShowChangeStoreModal] = useState(false);

  useEffect(() => {
    if (selectedStore) {
      if (firstStoreUpdate.current) {
        firstStoreUpdate.current = false;
        return;
      }

      // open modal
      if (initialStore.ID != selectedStore.ID) {
        tryJobStoreChange();
      }
    }
  }, [selectedStore]);

  const tryJobStoreChange = () => {
    if (props.formIsDirty) {
      setConfirmOptions({
        ...Helper.initialiseConfirmOptions(),
        showCancel: false,
        confirmButtonText: "Ok",
        display: true,
        heading: "Job has changes",
        text: "Cannot change store until changes have been saved"
      });
    } else {
      setShowChangeStoreModal(true);
    }
  };

  const onStoreChange = () => {
    setShowChangeStoreModal(false);
    props.refreshJob();
  };

  const onStoreChangeCancel = () => {
    setShowChangeStoreModal(false);
    setSelectedStore(initialStore);
  };

  const saveJobSignature = async () => {
    await props.saveJob();
    setUpdateSignature(updateSignature + 1);
  };

  const formButtonClick = () => {
    setShowFormsModal(true);
  };

  const [showFormsModal, setShowFormsModal] = useState(false);

  const formsClose = () => {
    setShowFormsModal(false);
    setUpdateForms(updateForms + 1);
  };

  // Job Rating

  const [feedbackList, setFeedbackList] = useState<any[]>();
  const [showJobRatings, setShowJobRatings] = useState(false);

  const getFeedback = async () => {
    const feedbackResponse = await FeedbackService.getFeedbackList(props.job.ID, Enums.Module.JobCard);
    let data = feedbackResponse.data;
    let total = feedbackResponse.total;

    if (total > 0) {
      data = Helper.sortObjectArrayOnDate(data, "CompletedDate", true);
      setShowJobRatings(true);
    } else {
      let hasFeedback = await FeedbackService.hasFeedbackForTenant();
      if (!hasFeedback) {
        setShowJobRatings(true);
      }
    }

    setFeedbackList(data);
  };


  const onSavingItems = (val: boolean) => {
    props.onSavingItems && props.onSavingItems(val);
    setSavingItems(val);
  }


  // OVERVIEW SECTIONS
  // A section only gets its own card once it has something in it. Until then, its add button sits in "Add to this job".

  const [appointmentCount, setAppointmentCount] = useState<number | null>(null);
  const [appointmentAddRequest, setAppointmentAddRequest] = useState(0);
  const [taskCounts, setTaskCounts] = useState<{ active: number, completed: number } | null>(null);
  const [taskAddRequest, setTaskAddRequest] = useState(0);
  const [linkedCount, setLinkedCount] = useState<number | null>(null);
  const [linkRequest, setLinkRequest] = useState(0);
  const [materialAddRequest, setMaterialAddRequest] = useState(0);
  const [assetAddRequest, setAssetAddRequest] = useState(0);

  const materialsCount = (props.jobInventoryUsed || []).filter((x: any) => x.IsActive).length;
  const assetsCount = (props.jobInventoryWorkedOn || []).filter((x: any) => x.IsActive).length;
  const canEdit = !!hasEmployee && !props.job.IsClosed;
  const canAddAssets = props.jobItemSelection !== undefined && props.jobItemSelection !== Enums.JobItemSelection.Disabled;

  const showMaterials = materialsCount > 0 || props.materialsRequired || !!props.inputErrors?.Materials;
  const showAssets = assetsCount > 0 || props.jobInventoryRequired || !!props.inputErrors?.JobInventory;
  const showTasks = props.tasksEnabled && (taskCounts?.active ?? 0) > 0;
  const showAppointments = (appointmentCount ?? 0) > 0;
  const showLinked = (linkedCount ?? 0) > 0;

  // JOB DETAILS: employees and van sit with the main fields, the account's other job fields behind "View additional fields"
  const isPrimaryField = (field: any) => field.Name === 'EmployeeList' || field.Name === 'Van';
  const isAdditionalField = (field: any) => !isPrimaryField(field)
      && field.JobStatusOptionName !== Enums.JobStatusOptionName.JobItem
      && field.JobStatusOptionName !== Enums.JobStatusOptionName.Materials;

  const fieldValue = (field: any) => props.job[JobStatusService.getFieldFromOption(field.JobStatusOptionName, props.job)];
  const isFilled = (field: any) => {
    const val = fieldValue(field);
    return val !== undefined && val !== null && val.toString() !== '';
  };
  // the same fields JobPropertiesColumn shows: switched on for the status, or already filled in
  const additionalFields = (props.jobProperties || []).filter((field: any) =>
      isAdditionalField(field) && field.Name !== 'Project' && field.JobStatusOptionName !== Enums.JobStatusOptionName.Description
      && (isFilled(field) || field.OptionConfiguration > Enums.OptionConfiguration.None));
  const additionalFilled = additionalFields.filter(isFilled).length;

  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  useEffect(() => {
    // open them when one of them fails validation
    if (additionalFields.some((field: any) => !!props.inputErrors?.[field.Name])) {
      setShowAdditionalFields(true);
    }
  }, [props.inputErrors]);

  const chip = (label: string, icon: React.ReactNode, onClick: () => void, disabled = false) =>
      <ChipButton key={label} leftSection={icon} onClick={onClick} disabled={disabled}>
        {label}
      </ChipButton>;

  const linkExistingButton = <Button variant={'subtle'} size={'compact-sm'} color={'scBlue'} leftSection={<IconLinkPlus size={16} />}
                                     onClick={() => setLinkRequest(x => x + 1)} disabled={!canEdit}>
    Link existing
  </Button>;

  const quoteChip = (label: string) => props.quotePermission ? chip(label, <IconFileDollar size={16} />, props.onCreateQuote, !canEdit) : null;
  const invoiceChip = (label: string) => props.invoicePermission ? chip(label, <IconFileInvoice size={16} />, props.onCreateInvoice, !canEdit) : null;
  const purchaseOrderChip = (label: string) => props.purchaseOrderPermission ? chip(label, <IconReceipt size={16} />, props.onCreatePurchaseOrder, !canEdit) : null;

  const workChips = [
    props.showStartTimer ? chip('Start timer', <IconClockPlay size={16} />, props.onStartTimer, props.timerBusy) : null,
    props.tasksEnabled && taskCounts !== null && taskCounts.active === 0 ? chip('Tasks', <IconCheckbox size={16} />, () => setTaskAddRequest(x => x + 1)) : null,
    appointmentCount === 0 ? chip('Appointments', <IconCalendarPlus size={16} />, () => setAppointmentAddRequest(x => x + 1)) : null,
    !showMaterials ? chip('Materials and services', <IconPackage size={16} />, () => setMaterialAddRequest(x => x + 1)) : null,
    !showAssets && canAddAssets ? chip('Customer assets', <IconDeviceDesktop size={16} />, () => setAssetAddRequest(x => x + 1)) : null,
    chip('Signatures', <IconSignature size={16} />, () => setShowSignaturesModal(true), !editJobPermission),
  ].filter(Boolean);

  const documentChips = linkedCount === 0 ? [quoteChip('Quotes'), invoiceChip('Invoices'), purchaseOrderChip('Purchase orders')].filter(Boolean) : [];

    return (
        <>
                    <Flex direction={'column'} gap={14} className={styles.inputMaxWidthOverride}>
                            <JobSectionCard title={'Customer Details'} alwaysOpenOnMobile
                                            aside={hasStockControl && manageCostingPermission ? {
                                                title: 'Costing',
                                                // clicking a profit figure opens the Costing tab
                                                content: <JobInventoryInvoicedWidgetLite job={props.job} onClick={() => props.setTab('Costing')} />
                                            } : undefined}>
                                <CustomerContactLocationSelector selectedCustomer={props.customer}
                                                                 setSelectedCustomer={props.setCustomer}
                                                                 canChangeCustomer={false}
                                                                 selectedContact={props.contact}
                                                                 setSelectedContact={updateContact}
                                                                 selectedLocation={props.location}
                                                                 setSelectedLocation={updateLocation}
                                                                 detailsView={false} module={Enums.Module.JobCard as any}
                                                                 inputErrors={props.inputErrors}
                                                                 accessStatus={props.accessStatus}
                                                                 canEdit={hasEmployee && !props.job.IsClosed}
                                                                 iconMode
                                                                 canEditCustomerInNormalView={true}
                                                                 mt={0 as any} {...{} as any}
                                />
                            </JobSectionCard>

                            <JobSectionCard title={'Job Details'} revealOnMobile={Object.values(props.inputErrors ?? {}).some(Boolean)}>
                                {/* grid order: job type, start date, assigned employees (full row), store, van, description (full row) */}
                                <div className={styles.detailsGrid}>
                                    <Box style={{order: 1}}>
                                        <SCComboBox
                                            mt={0}
                                            addOption={(masterOfficeAdminPermission ? {
                                                text: 'Add new job type',
                                                action: () => addNewJobType()
                                            } : undefined)}
                                            label="Job type"
                                            canSearch={false}
                                            options={jobTypes}
                                            placeholder="Select job type"
                                            value={(() => jobTypes?.find((x: any) => x.ID === props.job.JobTypeID))()}
                                            dataItemKey="ID"
                                            textField='Name'
                                            canClear={false}
                                            onChange={setSelectedJobType}
                                        />
                                    </Box>
                                    <Box style={{order: 2}}>
                                        <SCDatePicker
                                            mt={0}
                                            label='Start date'
                                            required={true}
                                            value={startDate}
                                            disabled={!hasEmployee || props.job.IsClosed || !editJobPermission}
                                            changeHandler={handleDateChange}
                                        />
                                    </Box>

                                    {isMultiStore && !Helper.isNullOrUndefined(selectedStore) &&
                                        <Box style={{order: 4}}>
                                            <StoreSelector
                                                accessStatus={props.accessStatus}
                                                disabled={!storeChangePermission}
                                                setSelectedStore={setSelectedStore}
                                                selectedStore={selectedStore}
                                                options={stores as any}
                                                canClear={false}
                                                canSearch={false}
                                                {...{} as any}
                                            />
                                        </Box>
                                    }

                                    <JobPropertiesColumn job={props.job} jobProperties={props.jobProperties}
                                                         updateJobProperty={handleJobPropertyChange} inputErrors={props.inputErrors}
                                                         showAll={true} customFields={props.customFields}
                                                         selectedStore={props.job.Store}
                                                         allowNonEmployee={false} accessStatus={props.accessStatus}
                                                         disabled={!editJobPermission} cypressCustomField={props.cypressCustomField}
                                                         hasVans={props.hasVans} inline
                                                         fieldFilter={isPrimaryField}
                                                         fieldClassName={(field: any) => field.Name === 'EmployeeList' ? styles.employeesField : styles.vanField}
                                                         {...{} as any}
                                    />

                                    <Box className={styles.fullRow} style={{order: 6}}>
                                        <ScTextAreaControl
                                            mt={0}
                                            label="Description"
                                            maw={'100%'}
                                            autosize
                                            minRows={3}
                                            maxRows={15}
                                            value={jobDesc}
                                            readOnly={!hasEmployee || props.job.IsClosed || !editJobPermission}
                                            required={true}
                                            error={props.inputErrors.JobDescription}
                                            onChange={handleJobDescChange}
                                            showCountMax={JobCardLengths.Description}
                                        />
                                    </Box>
                                </div>

                                {additionalFields.length > 0 && <>
                                    <UnstyledButton className={styles.additionalToggle} mt={10} aria-expanded={showAdditionalFields}
                                                    onClick={() => setShowAdditionalFields(x => !x)}>
                                        <IconChevronRight size={16} className={styles.additionalToggleIcon} />
                                        <span>{showAdditionalFields ? 'Hide additional fields' : 'View additional fields'}</span>
                                        {!showAdditionalFields && <Text component={'span'} inherit c={'gray.6'} fw={400}>· {additionalFilled} of {additionalFields.length} filled</Text>}
                                    </UnstyledButton>
                                    <Collapse in={showAdditionalFields}>
                                        <div className={`${styles.detailsGrid} ${styles.additionalFields}`}>
                                            <JobPropertiesColumn job={props.job} jobProperties={props.jobProperties}
                                                                 updateJobProperty={handleJobPropertyChange} inputErrors={props.inputErrors}
                                                                 showAll={true} customFields={props.customFields}
                                                                 selectedStore={props.job.Store}
                                                                 allowNonEmployee={false} accessStatus={props.accessStatus}
                                                                 disabled={!editJobPermission} cypressCustomField={props.cypressCustomField}
                                                                 hasVans={props.hasVans} inline
                                                                 fieldFilter={isAdditionalField}
                                                                 {...{} as any}
                                            />
                                        </div>
                                    </Collapse>
                                </>}
                            </JobSectionCard>

                            <JobSectionCard title={'Appointments'} count={appointmentCount || undefined} hidden={!showAppointments}>
                                <CardedAppointmentWidget
                                    storeID={props.job.StoreID}
                                    jobId={props.job.ID}
                                    customerID={props.customer?.ID}
                                    selectedEmployees={props.appointmentEmployees}
                                    selectedStore={props.job.Store}
                                    hideAddCard
                                    addRequest={appointmentAddRequest}
                                    onCountChange={(count: number) => {
                                        setAppointmentCount(count);
                                        props.onAppointmentRefresh && props.onAppointmentRefresh();
                                    }}
                                />
                                <ChipButton mt={'md'} leftSection={<IconPlus size={16} />} onClick={() => setAppointmentAddRequest(x => x + 1)}>
                                    Add appointments
                                </ChipButton>
                            </JobSectionCard>

                            <JobSectionCard title={'Materials and Services'} count={materialsCount || undefined} hidden={!showMaterials}>
                                <ManageJobInventoryComponent
                                    accessStatus={props.accessStatus}
                                    allowNonEmployee={false}
                                    inputErrors={{JobInventory: props.inputErrors?.Materials}}
                                    customerZone={false}
                                    fromCreateJob={false}
                                    fromStatusChange={false}
                                    job={props.job}
                                    jobInventoryUsed={props.jobInventoryUsed}
                                    jobInventoryWorkedOn={props.jobInventoryWorkedOn}
                                    stockItemStatus={Enums.StockItemStatus.ItemUsed}
                                    chipButtons
                                    key={'materialsSectionInAccordion'}
                                    onUpdate={(rowVersion: any, jobInventory: any, stockItemStatus: any) => props.updateJobInventory(rowVersion, jobInventory, stockItemStatus)}
                                    onCreateInvoice={props.onCreateInvoice}
                                    onSavingItems={onSavingItems}
                                    formIsDirty={props.formIsDirty}
                                    addItemRequest={materialAddRequest}
                                />
                            </JobSectionCard>

                            <JobSectionCard title={'Customer Assets'} count={assetsCount || undefined} hidden={!showAssets}>
                                <ManageJobInventoryComponent
                                    accessStatus={props.accessStatus}
                                    allowNonEmployee={false}
                                    inputErrors={props.inputErrors}
                                    customerZone={false}
                                    fromCreateJob={false}
                                    fromStatusChange={false}
                                    job={props.job}
                                    jobInventoryUsed={props.jobInventoryUsed}
                                    jobInventoryWorkedOn={props.jobInventoryWorkedOn}
                                    stockItemStatus={Enums.StockItemStatus.WorkedOn}
                                    chipButtons
                                    key={10}
                                    onUpdate={(rowVersion: any, jobInventory: any, stockItemStatus: any) => props.updateJobInventory(rowVersion, jobInventory, stockItemStatus)}
                                    onSavingItems={props.onSavingItems}
                                    formIsDirty={props.formIsDirty}
                                    addItemRequest={assetAddRequest}
                                />
                            </JobSectionCard>

                            {props.tasksEnabled &&
                                <JobSectionCard title={'Tasks'} hidden={!showTasks}
                                                count={taskCounts && taskCounts.active > 0 ? `${taskCounts.completed} of ${taskCounts.active} done` : undefined}>
                                    <TaskItems module={Enums.Module.JobCard} data={props.job} accessStatus={props.accessStatus} updateSignatures={updateSignature} updateForms={updateForms}
                                               requiredFormDefinitions={props.job.JobType ? props.job.JobType.FormDefinitions : []}
                                               addRequest={taskAddRequest}
                                               onCountChange={(active: number, completed: number) => setTaskCounts({active, completed})} />
                                </JobSectionCard>
                            }

                            <JobSectionCard title={'Financials'} count={linkedCount || undefined} hidden={!showLinked} headerRight={linkExistingButton}>
                                {/* Linked items list (project, queries, quotes, invoices, purchase orders) */}
                                <LinkedItemsList
                                    jobId={props.job.ID}
                                    project={props.job.Project}
                                    projectId={props.job.ProjectID}
                                    customerId={props.customer?.ID}
                                    onChangeProject={(project: any) => props.updateJob(null, null, [
                                        { key: 'ProjectID', value: project ? project.ID : null },
                                        { key: 'Project', value: project ?? null },
                                    ])}
                                    onOpenQuote={props.onOpenQuote}
                                    onOpenInvoice={props.onOpenInvoice}
                                    onOpenPurchaseOrder={props.onOpenPurchaseOrder}
                                    refreshDeps={props.linkedItemsRefreshDeps}
                                    currencySymbol={currencySymbol}
                                    onTotalCountChange={setLinkedCount}
                                    linkRequest={linkRequest}
                                    hideAddLink
                                />
                                <Flex gap={'xs'} wrap={'wrap'} mt={'md'}>
                                    {quoteChip('Add quotes')}
                                    {invoiceChip('Add invoices')}
                                    {purchaseOrderChip('Add purchase orders')}
                                </Flex>
                            </JobSectionCard>

                            {canEdit && (workChips.length > 0 || documentChips.length > 0) &&
                                <JobSectionCard title={'Add to this job'} headerRight={linkedCount === 0 ? linkExistingButton : undefined}>
                                    <Flex gap={'xs'} wrap={'wrap'}>{workChips}{documentChips}</Flex>
                                </JobSectionCard>
                            }

                            <JobSectionCard title={'Comments'}>
                                <ItemComments
                                    itemID={props.job.ID}
                                    module={Enums.Module.JobCard}
                                    storeID={props.job.StoreID}
                                    triggerSave={props.triggerSaveComment}
                                    hideLabel
                                    {...{} as any}
                                />
                            </JobSectionCard>

                            {
                                showJobRatings &&
                                <JobSectionCard title={'Job rating'}>
                                    <JobRating jobCard={props.job} feedbackList={feedbackList}/>
                                </JobSectionCard>
                            }

                            {
                                hasEmployee &&
                                <JobHistory jobId={props.job.ID} retrigger={props.retriggerAuditLog}/>
                            }

                        {showFormsModal ?
                            <FormDefinitionsForItem
                                customer={props.job.Customer}
                                onClose={formsClose}
                                itemID={props.job.ID}
                                itemModule={Enums.Module.JobCard}
                                linkedFormDefinitions={props.job.JobType ? props.job.JobType.FormDefinitions : []}
                                onlyLinkedForms={props.job.JobType ? props.job.JobType.OnlyLinkedForms : true}
                                {...{} as any}
                            /> : ''
                        }

                        {showSignaturesModal ?
                            <ManageSignatures job={props.job} setJob={props.setJob} saveJob={saveJobSignature}
                                              setShowModal={setShowSignaturesModal}
                                              accessStatus={props.accessStatus}/> : ''
                        }

                    </Flex>
            {showManageJobType ?
                <ManageJobType isNew={true} onJobTypeSave={onJobTypeSave}
                               accessStatus={props.accessStatus} {...{} as any} /> : ''
            }

            {showManageInventory ?
                <ManageInventory isNew={true} isService={showManageInventory === "service"}
                                 onInventorySave={onInventorySave}
                                 addNewInventory={() => addNewInventory("service")}
                                 accessStatus={props.accessStatus} {...{} as any} /> : ''
            }

            {showChangeStoreModal ?
                <ChangeStore isRecurringJob={!Helper.isNullOrUndefined(props.job.JobScheduleID)} jobID={props.job.ID}
                             storeID={selectedStore ? selectedStore.ID : null} fromStoreName={storeSearch}
                             toStoreName={selectedStore ? selectedStore.Name : ''}
                             onStoreChange={onStoreChange} onCancel={onStoreChangeCancel}
                             accessStatus={props.accessStatus}/> : ''
            }

            <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions}/>
        </>
    );
}

export default JobDetails;
